import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Pagination from "@mui/material/Pagination"
import Paper from "@mui/material/Paper"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import Alt from "../components/Alt"
import Flag from "../components/Flag"
import Locations from "../components/Locations"
import PlayerName from "../components/PlayerName"
import TableLoading from "../components/TableLoading"
import TimeDetail from "../components/TimeDetail"
import Wheel from "../components/Wheel"
import environment from "../environment"
import { Game } from "../models/game"
import { speedMap } from "../models/speed"
import { trackMap } from "../models/track"
import { getLocationCookie, setLocationCookie } from "../utils/cookie"
import { milliToTime } from "../utils/milli-to-time"
import {
  createCustomSearchParams,
  equalSearchParams,
} from "../utils/search-params"

interface TrackRankingTitle {
  trackName: string
  speed: number
}

interface TrackRankingRow {
  time: string
  motion: boolean
  playerId: string
  playerName: string
  playerAlpha3: string
  playerNation: string
  date: string
  isAlt: boolean
  rank: string
  character: number
  variant: number
  miiWeightClass: number
  laps: string[]
  vehicle: number
  tire: number
  glider: number
  coins: string
  shrooms: string
  open: boolean
}

export default function TrackRanking() {
  let { game, tid } = useParams()
  const speeds = speedMap[game as Game]
  const tracks = trackMap[game as Game]

  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [trackRanking, setTrackRanking] = useState(
    null as unknown as TrackRankingTitle
  )
  const [speed, setSpeed] = useState(searchParams.get("speed") ?? "150")
  const [location, setLocation] = useState(
    searchParams.get("location") ?? getLocationCookie()
  )
  const [trackRankingRows, setTrackRankingRows] = useState(
    [] as TrackRankingRow[]
  )
  const [highlight, setHighlight] = useState(
    Number(searchParams.get("highlight"))
  )
  const [page, setPage] = useState(highlight ? Math.ceil(highlight / 100) : 1)

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const handleHighlight = (id: string) => {
    setTimeout(tryScroll, 0)

    function tryScroll() {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      })
    }
  }

  useEffect(() => {
    const loc = searchParams.get("location")
    if (loc) {
      setLocationCookie(loc)
      setLocation(loc)
    }
    const speed = searchParams.get("speed")
    if (speed) {
      setSpeed(speed)
    }
  }, [searchParams])

  useEffect(() => {
    const customSearchParams = createCustomSearchParams({
      highlight: highlight,
      location: location,
      speed: speed,
    })
    if (!equalSearchParams(customSearchParams, searchParams)) {
      setSearchParams(customSearchParams)
    }

    const trackRanking: TrackRankingTitle = {
      trackName: tracks.find((x) => x.id === Number(tid))?.name ?? "",
      speed: Number(speed) ?? "",
    }
    setTrackRanking(trackRanking)

    const offset = (page - 1) * 100
    const limit = 100
    fetch(
      `${environment.domain}/api/${game}/trs/${tid}?limit=${limit}&location=${location}&offset=${offset}&speed=${speed}`
    )
      .then((res) => res.json() as unknown as any)
      .then((res) => {
        const totalCount = res.totalCount
        const rows = res.rows as any[]
        let trackRankingRows: TrackRankingRow[] = []
        rows.forEach((res) => {
          trackRankingRows.push({
            time: res?.time ? res.time : "",
            motion: res?.motion,
            playerId: res?.pid ? res.pid : "",
            playerName: res?.name !== undefined ? res.name : "",
            playerAlpha3: res?.alpha_3 ? res.alpha_3 : "",
            playerNation: res?.country ? res.country : "",
            date: res?.date ? res.date.substring(0, 10) : "",
            isAlt: res?.is_alt,
            rank: res?.rank ? res.rank : "",
            character: res?.character,
            variant: res?.variant,
            miiWeightClass: res?.mii_weight_class,
            laps: res?.laps
              ? res.laps.map((lap: number) => milliToTime(lap))
              : [],
            vehicle: res?.vehicle,
            tire: res?.tire,
            glider: res?.glider,
            coins: res?.coins,
            shrooms: res?.shrooms,
            open: false,
          })
        })
        setTotalCount(totalCount)
        setTrackRankingRows(trackRankingRows)
        setLoading(false)
        if (
          highlight &&
          trackRankingRows.some((x) => Number(x.rank) === highlight)
        ) {
          handleHighlight(`rank${highlight}`)
          setHighlight(0)
        }
      })
  }, [location, game, tid, speed, page, speeds, tracks])

  const navigate = useNavigate()

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", mb: 3, alignItems: "center" }}>
        <Typography variant="h5">
          {trackRanking?.trackName}{" "}
          {speeds.length > 1 ? trackRanking?.speed + "cc" : ""}
        </Typography>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Locations
          game={game as Game}
          location={location}
          setLocation={setLocation}
        ></Locations>
      </Box>
      {loading ? (
        <TableLoading />
      ) : (
        <Box>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Pagination
              count={Math.ceil(totalCount / 100)}
              siblingCount={3}
              page={page}
              onChange={handleChange}
              sx={{ mx: "auto" }}
            />
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="trs">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Nation</TableCell>
                  <TableCell>Upload</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trackRankingRows.map((row, i) => (
                  <React.Fragment>
                    <TableRow
                      key={`${row.rank}${row.playerId}`}
                      id={`rank${row.rank}`}
                      sx={{
                        "& > *": { borderBottom: "unset" },
                        backgroundColor:
                          Number(searchParams.get("highlight")) ===
                          Number(row.rank)
                            ? "rgba(255, 255, 0, 0.25)"
                            : "none",
                      }}
                    >
                      <TableCell sx={{ border: "none" }}>{row.rank}</TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Link
                          component="a"
                          underline="none"
                          href={`/${game}/players/${
                            row.playerId
                          }?location=${getLocationCookie()}`}
                          onClick={(e) => {
                            navigate(
                              `/${game}/players/${
                                row.playerId
                              }?location=${getLocationCookie()}`
                            )
                            e.preventDefault()
                          }}
                        >
                          <PlayerName name={row.playerName} />
                        </Link>
                        {row.isAlt ? <Alt /> : null}
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        {row.time}
                        {row.motion ? <Wheel /> : ""}
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Flag
                          alpha3={row.playerAlpha3}
                          country={row.playerNation}
                          tooltip={true}
                        />
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>{row.date}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ paddingTop: 0, paddingBottom: 0, border: "none" }}
                      >
                        {row.date ? (
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            sx={{ height: "26px", width: "26px" }}
                            onClick={() => {
                              const newTrackRankingRows = [...trackRankingRows]
                              newTrackRankingRows[i].open = !row.open
                              setTrackRankingRows(newTrackRankingRows)
                            }}
                          >
                            {row.open ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TimeDetail
                      open={row.open}
                      trackId={Number(tid)}
                      playerId={row.playerId}
                      game={game as Game}
                      character={row.character}
                      variant={row.variant}
                      miiWeightClass={row.miiWeightClass}
                      laps={row.laps}
                      vehicle={row.vehicle}
                      tire={row.tire}
                      glider={row.glider}
                      coins={row.coins}
                      shrooms={row.shrooms}
                    ></TimeDetail>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Pagination
              count={Math.ceil(totalCount / 100)}
              siblingCount={10}
              page={page}
              onChange={handleChange}
              sx={{ mx: "auto" }}
            />
          </Stack>
        </Box>
      )}
    </Box>
  )
}
